/**
 * MountainRange.js
 *
 * The layered horizon at the foot of the landing sky: a faceted far range, a
 * rolling middle range, a meadow, and five conifers at the lower right.
 *
 * Every path, fill, and transform is copied verbatim from the approved
 * reference (docs/design/landing-sky/landing-sky-reference.html). Purely
 * decorative — hence aria-hidden.
 *
 * The five conifers used to live here too. They now have their own SVG
 * (ConiferStand.js) because this one stretches and they must not — see below.
 *
 * RESPONSIVE NOTE
 * ---------------
 * The reference is a fixed 1280-wide composition and these paths are authored
 * in that space. Here the SVG spans 100% of the width at a fixed height with
 * preserveAspectRatio="none", so:
 *
 *   - at 1280px wide it is pixel-identical to the reference, and
 *   - at any other width it stretches horizontally, holding the horizon line
 *     at a constant height above the bottom edge.
 *
 * Holding the height constant is the deliberate choice: letting the range scale
 * proportionally would push the horizon up over the hero text on wide screens.
 * Stretching a ridgeline is harmless — a hill is still a hill 20% wider — which
 * is why only the ranges, the meadow, and the rim light remain in this file.
 */

import React from 'react';

const MountainRange = () => (
  <svg
    className="landing-sky-range"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 1280 230"
    preserveAspectRatio="none"
    aria-hidden="true"
    focusable="false"
  >
    <defs>
      {/* Suffixed id: this SVG is inlined into the document, so its defs share
          one global id space with every other inline SVG on the page. */}
      <linearGradient id="landingSkyFarRange" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#1E4550" />
        <stop offset="1" stopColor="#132E38" />
      </linearGradient>
    </defs>

    {/* Far range: faceted peaks, with a faint rim light along the ridge. */}
    <path
      d="M0 122 L60 112 L110 96 L150 82 L172 92 L204 86 L250 104 L300 114 L360 120 L420 111 L468 98 L500 105 L560 118 L640 124 L720 120 L790 107 L850 90 L900 72 L924 83 L950 78 L1000 96 L1050 108 L1110 101 L1160 88 L1194 97 L1240 109 L1280 114 L1280 230 L0 230 Z"
      fill="url(#landingSkyFarRange)"
    />
    <path
      d="M0 122 L60 112 L110 96 L150 82 L172 92 L204 86 L250 104 L300 114 L360 120 L420 111 L468 98 L500 105 L560 118 L640 124 L720 120 L790 107 L850 90 L900 72 L924 83 L950 78 L1000 96 L1050 108 L1110 101 L1160 88 L1194 97 L1240 109 L1280 114"
      fill="none"
      stroke="rgba(170,222,226,.16)"
      strokeWidth="1"
      strokeLinejoin="round"
    />

    {/* Middle range: rolling hills. */}
    <path
      d="M0 160 C 80 156, 150 150, 230 140 C 290 132, 330 128, 380 132 C 440 137, 500 150, 580 158 C 660 165, 760 166, 860 162 C 960 158, 1040 150, 1120 152 C 1190 154, 1240 158, 1280 160 L1280 230 L0 230 Z"
      fill="#0C1D25"
    />

    {/* Meadow. */}
    <path
      d="M0 182 C 160 178, 360 180, 560 181 C 760 182, 960 177, 1120 178 C 1200 179, 1250 180, 1280 180 L1280 230 L0 230 Z"
      fill="#060F14"
    />
  </svg>
);

export default MountainRange;
